import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTab, setUserRole, setTabsFromSheets } from '../../../redux/projectTabsSlice';
import { loadEditorConfig, selectSheetTypes, selectPartyTypes, selectEditorConfigLoaded, setActiveParty } from '../../../redux/editorConfigSlice';
import './ProjectSubBar.css';

const ProjectSubBar = ({ projectName }) => {
  const dispatch = useDispatch();
  const tabs = useSelector((state) => state.projectTabs.tabs);
  const activeTabId = useSelector((state) => state.projectTabs.activeTabId);
  const enabledPaftaIds = useSelector((state) => state.projectTabs.enabledPaftaIds);
  const userRole = useSelector((state) => state.projectTabs.userRole);
  const tabsRef = useRef(null);

  // Backend config
  const configLoaded = useSelector(selectEditorConfigLoaded);
  const sheetTypes = useSelector(selectSheetTypes);
  const partyTypes = useSelector(selectPartyTypes);
  const activePartyId = useSelector((state) => state.editorConfig.activePartyId);
  const shareAccess = useSelector((state) => state.project.shareAccess);
  const isShareMode = !!shareAccess;

  // Load discipline config from backend on mount (skip if hydrated from share)
  useEffect(() => {
    if (isShareMode) return; // Share mode: config already hydrated from share token
    const token = localStorage.getItem('projabze_token');
    if (token && !configLoaded) {
      dispatch(loadEditorConfig());
    }
  }, [dispatch, configLoaded, isShareMode]);

  // When sheet types arrive from backend, update project tabs
  useEffect(() => {
    if (sheetTypes.length > 0) {
      dispatch(setTabsFromSheets(sheetTypes));
    }
  }, [sheetTypes, dispatch]);

  // Derive role options from party types if available
  const roleOptions = useMemo(() => {
    if (partyTypes.length > 0) {
      return partyTypes.map(p => ({ id: p.id, label: p.name }));
    }
    return null;
  }, [partyTypes]);

  // Proje adından uzantıyı çıkar (örn. "örnek-5.dxf" → "örnek-5")
  const baseName = useMemo(() => {
    if (!projectName) return '';
    return projectName.replace(/\.[^.]+$/, '');
  }, [projectName]);

  const handleWheel = useCallback((e) => {
    if (tabsRef.current) {
      e.preventDefault();
      tabsRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  // Sadece enabled olan tab'ları göster
  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => enabledPaftaIds.includes(tab.id));
  }, [tabs, enabledPaftaIds]);

  // Handle role/party change
  const handleRoleChange = useCallback((e) => {
    const value = e.target.value;
    if (roleOptions) {
      // Backend-driven: set active party
      dispatch(setActiveParty(value));
      // Also update userRole for legacy components
      const party = partyTypes.find(p => p.id === value);
      if (party) dispatch(setUserRole(party.name));
    } else {
      dispatch(setUserRole(value));
    }
  }, [dispatch, roleOptions, partyTypes]);

  return (
    <div className="project-sub-bar">
      <div className="psb-tabs" ref={tabsRef} onWheel={handleWheel}>
        {visibleTabs.map((tab) => {
          const fileName = tab.suffix
            ? `${baseName}${tab.suffix}.dxf`
            : `${baseName}.dxf`;

          return (
            <button
              key={tab.id}
              className={`psb-tab${activeTabId === tab.id ? ' active' : ''}`}
              onClick={() => dispatch(setActiveTab(tab.id))}
              title={`${tab.label} - ${fileName}`}
            >
              <span className="psb-tab-icon">{tab.icon}</span>
              {tab.label}
              <svg className="psb-tab-chevron" viewBox="0 0 12 10" width="12" height="10">
                <path d="M1 1l3.5 4-3.5 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6.5 1l3.5 4-3.5 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {fileName}
            </button>
          );
        })}
      </div>

    </div>
  );
};

export default ProjectSubBar;
